import React from 'react';
import { Phone, Mail, MapPin, Instagram, MessageCircle } from 'lucide-react';

/**
 * ContactSection — blocco contatti mostrato nella pagina pubblica /:companySlug/contatti.
 *
 * Riceve `settings` già caricati da ContactPage (nessuna lettura Firebase propria,
 * per evitare una doppia sottoscrizione allo stesso path).
 *
 * Stessa logica di fallback usata nel footer di PublicCarList: i campi pubblici
 * in settings.sito hanno priorità, altrimenti si ripiega sui dati aziendali in
 * settings.azienda (telefono/email/indirizzo "interni").
 */
const ContactSection = ({ settings, accentColor }) => {
  const az = settings?.azienda || {};
  const si = settings?.sito || {};
  const accent = accentColor || si.coloreAccento || '#2563EB';

  const telefono = si.telefonoPubblico || az.telefono;
  const email = si.emailPubblica || az.email;
  const indirizzo = si.indirizzoPubblico || az.indirizzo;
  const whatsapp = si.whatsapp;
  const instagram = si.instagram;

  const contatti = [
    telefono && {
      key: 'telefono',
      icon: Phone,
      label: 'Telefono',
      value: telefono,
      href: `tel:${telefono.replace(/\s+/g, '')}`,
    },
    whatsapp && {
      key: 'whatsapp',
      icon: MessageCircle,
      label: 'WhatsApp',
      value: whatsapp,
      href: `https://wa.me/${(() => {
        const raw = String(whatsapp).trim();
        const digits = raw.replace(/\D/g, '');
        if (raw.startsWith('+')) return digits;
        if (digits.startsWith('00')) return digits.slice(2);
        if (digits.startsWith('39')) return digits;
        return `39${digits}`;
      })()}`,
      external: true,
    },
    email && {
      key: 'email',
      icon: Mail,
      label: 'Email',
      value: email,
      href: `mailto:${email}`,
    },
    instagram && {
      key: 'instagram',
      icon: Instagram,
      label: 'Instagram',
      value: instagram,
      href: `https://instagram.com/${instagram.replace('@', '')}`,
      external: true,
    },
  ].filter(Boolean);

  const hasAnyContact = contatti.length > 0 || Boolean(indirizzo);

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 sm:py-14">
      <div className="text-center mb-10">
        <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight mb-2">
          Contattaci
        </h2>
        <div className="w-12 h-1 mx-auto rounded-full" style={{ background: accent }} />
      </div>

      {!hasAnyContact ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-10 text-center">
          <p className="text-slate-500">
            I recapiti di questa attività non sono ancora stati impostati.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {contatti.map(({ key, icon: Icon, label, value, href, external }) => (
            <a
              key={key}
              href={href}
              target={external ? '_blank' : undefined}
              rel={external ? 'noopener noreferrer' : undefined}
              className="flex items-center gap-4 bg-white rounded-2xl shadow-sm border border-slate-100 p-5 hover:shadow-md transition"
              style={{ textDecoration: 'none' }}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${accent}1A`, color: accent }}
              >
                <Icon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-black uppercase tracking-widest text-slate-400">{label}</div>
                <div className="text-sm sm:text-base font-semibold text-slate-800 truncate">{value}</div>
              </div>
            </a>
          ))}

          {indirizzo && (
            <div className="flex items-center gap-4 bg-white rounded-2xl shadow-sm border border-slate-100 p-5 sm:col-span-2">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${accent}1A`, color: accent }}
              >
                <MapPin className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-black uppercase tracking-widest text-slate-400">Indirizzo</div>
                <div className="text-sm sm:text-base font-semibold text-slate-800">{indirizzo}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mappa — embed pubblico di Google Maps, nessuna API key richiesta.
          Mostrata solo se l'azienda ha impostato un indirizzo. */}
      {indirizzo && (
        <div className="mt-4 rounded-2xl overflow-hidden border border-slate-100 shadow-sm" style={{ height: '320px' }}>
          <iframe
            title="Dove siamo"
            className="w-full h-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://www.google.com/maps?q=${encodeURIComponent(indirizzo)}&output=embed`}
          />
        </div>
      )}
    </div>
  );
};

export default ContactSection;
